import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, signal, inject, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MessageService, ConfirmationService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Select } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { Toast } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FormDropdownService } from '../services/form-dropdown.service';
import { ProfessionalsService } from '../services/professionals.service';
import { ProfessionalPreRegistration } from '../models/models';
import { RegistrationInvitationService, RegistrationInvitation } from '../services/registration-invitation.service';
import { AuthService } from '../services/auth.service';
import { UserSessionService } from '../services/mock-auth.service';
import { AppUser, AppRole, createAppUserFromToken } from '../models/permissions.model';

type RegistrationMode = 'DIRECT' | 'DELEGATED' | null;

// Form model for handling UI state (allows string types for form binding)
// Excludes boolean-only fields like forceSave that don't need string representation
type PreRegisterForm = {
  [K in keyof Omit<Required<ProfessionalPreRegistration>, 'forceSave'>]: string;
};

@Component({
  selector: 'app-pre-registration',
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TagModule,
    ProgressSpinnerModule,
    InputTextModule,
    Toast,
    BreadcrumbModule,
    Select,
    CardModule,
    TooltipModule,
    MessageModule,
    ConfirmDialogModule,
  ],
  templateUrl: './pre-registration.component.html',
  styleUrl: './pre-registration.component.scss',
})
export class PreRegistrationComponent implements OnInit {
  private titleService = inject(Title);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private formDropdownService = inject(FormDropdownService);
  private professionalsService = inject(ProfessionalsService);
  private invitationService = inject(RegistrationInvitationService);
  private authService = inject(AuthService); // Real CIAM authentication
  private mockAuthService = inject(UserSessionService); // Mock for development
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  /** Se aperto da review-professional per una sede operativa */
  parentId = signal<string>('');
  /** URL a cui tornare dopo il successo (es. /axa/review-professional/BSP-001) */
  returnToUrl = signal<string>('');

  // ViewChild for accessing the template-driven form
  @ViewChild('preRegistrationForm') preRegistrationForm!: NgForm;

  // User context (NOW uses AppUser from permissions.model)
  currentUser = signal<AppUser | null>(null);
  isAXAUser = computed(() => {
    const user = this.currentUser();
    // Use AppRole enum values for type safety
    const axaRoles = [AppRole.SUPER_ADMIN, AppRole.BACKOFFICE_ADMIN, AppRole.BACKOFFICE_PROFESSIONAL];
    return user !== null && user.roles.some(role => axaRoles.includes(role as AppRole));
  });
  isFiduciarioUser = computed(() => {
    const user = this.currentUser();
    return user !== null && user.roles.includes(AppRole.PROFESSIONAL as any);
  });
  isProfessionalUser = computed(() => {
    const user = this.currentUser();
    return user !== null && user.roles.includes(AppRole.PROFESSIONAL as any);
  });

  // AXA-specific: Mode selection and invitation
  registrationMode = signal<RegistrationMode>(null);
  email = signal<string>('');
  confirmEmail = signal<string>('');
  givenName = signal<string>(''); // Nome fiduciario
  familyName = signal<string>(''); // Cognome fiduciario
  generatedInvitation = signal<RegistrationInvitation | null>(null);
  isLoading = signal<boolean>(false);

  private pendingForceSavePayload: ProfessionalPreRegistration | null = null;

  // Loading state for checking registration status
  isCheckingStatus = signal<boolean>(true);
  showForm = signal<boolean>(false);

  // Initialize with a complete default object structure
  preRegister = signal<PreRegisterForm>({
    type: '',
    bodyShopType: '',
    firstName: '',
    lastName: '',
    fiscalCode: '',
    vatNumber: '',
    registrationEmail: '',
    confirmRegistrationEmail: '',
    legalOrPhysical: '',
    companyType: '',
    businessName: '',
    parentProfessionalId: this.parentId() || '',
  });

  tipologieOptions = signal<Array<{ name: string; code: string }>>([]);

  bodyworkOptions = signal<Array<{ name: string; code: string }>>([]);

  personTypeOptions = signal<Array<{ value: string; label: string }>>([]);
  companyTypeOptions = signal<Array<{ value: string; label: string; disabled?: boolean }>>([]);

  filteredSupplierTypes = computed(() => {
    const personType = this.preRegister().legalOrPhysical;
    const companyTypes = this.companyTypeOptions();

    if (personType === 'FISICA') {
      return companyTypes.filter((item) => item.value === 'DI');
    }

    if (personType === 'GIURIDICA') {
      return companyTypes.filter((item) => item.value !== 'DI');
    }

    return [];
  });

  isBodyShopSelected = computed(() =>
    ['BODYSHOP'].includes(this.preRegister().type),
  );

  // Conditional required fields based on person type (FISICA or GIURIDICA)
  isBusinessNameRequired = computed(() => this.preRegister().legalOrPhysical === 'GIURIDICA' || this.preRegister().legalOrPhysical === 'FISICA');
  // Nome e cognome del rappresentante legale sono sempre obbligatori: alimentano
  // la sezione "Anagrafica Rappresentante Legale" dei Dati Generali.
  isFirstNameRequired = computed(() => true);
  isLastNameRequired = computed(() => true);
  isFiscalCodeRequired = computed(() => this.preRegister().legalOrPhysical === 'FISICA');
  isVatNumberRequired = computed(() => this.preRegister().legalOrPhysical === 'GIURIDICA');

  // AXA-specific: Select registration mode
  selectMode(mode: RegistrationMode) {
    this.registrationMode.set(mode);
    
    if (mode === 'DIRECT') {
      this.messageService.add({
        severity: 'info',
        summary: 'Modalità Diretta',
        detail: 'Il fiduciario riceverà il link e compilerà i dati autonomamente',
        life: 4000,
      });
    } else if (mode === 'DELEGATED') {
      this.messageService.add({
        severity: 'info',
        summary: 'Modalità Delegata',
        detail: 'Compila i dati per conto del fiduciario prima di generare il link',
        life: 4000,
      });
      // Show form for AXA to fill in delegated mode
      this.showForm.set(true);
    }
  }

  // AXA-specific: Back to mode selection
  backToModeSelection() {
    // Se siamo arrivati da review-professional, torna lì invece che alla selezione
    if (this.returnToUrl()) {
      this.router.navigate([this.returnToUrl()]);
      return;
    }
    this.registrationMode.set(null);
    this.showForm.set(false);
    this.email.set('');
    this.confirmEmail.set('');
    this.givenName.set('');
    this.familyName.set('');
    this.resetFormData();
  }

  // Helper to handle changes and reset the dependent field
  onFiduciarioChange(type: string) {
    this.preRegister.update((state) => ({
      ...state,
      type,
      bodyShopType: ['BODYSHOP'].includes(type) ? state.bodyShopType : '',
    }));
  }

  onBodyShopTypeChange(bodyShopType: string) {
    this.preRegister.update((state) => ({
      ...state,
      bodyShopType,
    }));
  }

  onPersonTypeChange(legalOrPhysical: string) {
    this.preRegister.update((state) => {
    let companyType = state.companyType;

    if (legalOrPhysical === 'FISICA' && companyType !== 'DI') {
      companyType = '';
    }

    if (legalOrPhysical === 'GIURIDICA' && companyType === 'DI') {
      companyType = '';
    }

    if (!legalOrPhysical) {
      companyType = '';
    }

    return {
      ...state,
      legalOrPhysical,
      companyType,
    };
    });
  }

  onCompanyTypeChange(companyType: string) {
  this.preRegister.update((state) => ({
    ...state,
    companyType: companyType || '',
  }));
}

  updatePreRegistrationFiscalCode(value: string): void {
    this.preRegister.update((state) => ({
      ...state,
      fiscalCode: (value || '').toUpperCase(),
    }));
  }

  ngOnInit() {
    // WCAG 9.2.4.2: Set descriptive page title
    this.titleService.setTitle('Pre-registrazione Fiduciario - Portale Fiduciari AXA');
    
    // ========================================
    // Get current user from either AuthService (real CIAM) or MockAuthService (dev)
    let user: AppUser | null = null;
    
    // Try real AuthService first (CIAM token)
    const authProfile = this.authService.getCurrentUser();
    if (authProfile) {
      user = createAppUserFromToken(authProfile); // Convert UserProfile to AppUser
    } else {
      // Fallback to MockAuthService (development)
      user = this.mockAuthService.getCurrentUser();
    }
    
    this.currentUser.set(user);
    
    // Debug: Log user and roles to verify
    console.log('🔍 Pre-registration - Current User:', user);
    console.log('🔍 User roles:', user?.roles);
    console.log('🔍 isAXAUser():', this.isAXAUser());
    console.log('🔍 isProfessionalUser():', this.isProfessionalUser());
    
    // If user is professional, pre-populate email from their CIAM profile
    if (this.isProfessionalUser() && user?.email) {
      console.log('✅ Pre-populating email for professional user:', user.email);
      this.preRegister.update((state) => ({
        ...state,
        registrationEmail: user.email || '',
        confirmRegistrationEmail: user.email || ''
      }));
    }
    
    // Controlla query params per delegated pre-registration da review-professional
    // (indipendente dal ruolo utente, funziona anche in dev con mock user)
    const params = this.route.snapshot.queryParams;
    const isDelegatedFromParent = params['mode'] === 'DELEGATED' && !!params['parentId'];

    if (isDelegatedFromParent) {
      this.parentId.set(params['parentId']);
      this.returnToUrl.set(params['returnTo'] ? decodeURIComponent(params['returnTo']) : '');
      this.registrationMode.set('DELEGATED');
      this.showForm.set(true);
      this.isCheckingStatus.set(false);
      this.loadTipologieOptions();
      this.loadBodyworkOptions();
      this.loadPersonTypeOptions();
      this.loadCompanyTypeOptions();

      this.professionalsService.getProfessionalGeneralData(params['parentId']).subscribe({
        next: (response: any) => {
          const d = response?.data ?? {};
          const c = d.companyData ?? {};
          this.preRegister.update(state => ({
            ...state,
            type:                     d.professionalType    ?? state.type,
            bodyShopType:             d.bodyShopType        ?? state.bodyShopType,
            businessName:             c.businessName        ?? state.businessName,
            firstName:                c.firstname           ?? state.firstName,
            lastName:                 c.lastname            ?? state.lastName,
            fiscalCode:               c.fiscalCode          ?? state.fiscalCode,
            vatNumber:                c.vatNumber           ?? state.vatNumber,
            legalOrPhysical:          c.legalOrPhysical     ?? state.legalOrPhysical,
            companyType:              c.companyType         ?? state.companyType,
            parentProfessionalId:     this.parentId()       ?? this.parentId(),
            registrationEmail:        '',
            confirmRegistrationEmail: '',
          }));
        },
        error: () => { /* form resta vuoto ma funzionante */ }
      });
      return; // skip il resto del flow normale
    }

    if (this.isAXAUser()) {
      // AXA user: Show mode selection first
      this.isCheckingStatus.set(false);
      this.loadTipologieOptions();
      this.loadBodyworkOptions();
      this.loadPersonTypeOptions();
      this.loadCompanyTypeOptions();
    } else {
      // Fiduciario user: Check status and show form if needed
      this.checkRegistrationStatus();
    }
  }

  /**
   * Check if user has already completed pre-registration
   * If yes, redirect to next step
   * If no, show the form
   * 
   * For PROFESSIONAL users: calls /api/v1/auth/profile to check if professionalId exists
   * - If exists (200 OK) -> redirect to registration-progress
   * - If not exists (404 NOT_FOUND) -> show form
   */
  private checkRegistrationStatus() {
    // For professional users: check via API if professionalId already exists
    if (this.isProfessionalUser()) {
      console.log('🔍 [Pre-Registration] Checking if professional user already has a profile...');
      
      // Determine whether to use real API or mock based on token presence
      const hasRealToken = !!localStorage.getItem('access_token');
      const profileObservable = hasRealToken
        ? this.professionalsService.getAuthProfile()
        : this.mockAuthService.simulateGetAuthProfile();
      
      profileObservable.subscribe({
        next: (response) => {
          // Professional profile exists - redirect to registration progress
          console.log('✅ [Pre-Registration] Professional profile found:', response);
          this.isCheckingStatus.set(false);
          
          this.messageService.add({
            severity: 'info',
            summary: 'Profilo Esistente',
            detail: 'Hai già completato la pre-registrazione. Reindirizzamento...',
            life: 3000,
          });
          
          setTimeout(() => {
            this.router.navigate(['/axa/professionals-registration-progress']);
          }, 1500);
        },
        error: (err) => {
          // Check if it's NOT_FOUND (user can proceed with pre-registration)
          if (err?.error?.errorCode === 'NOT_FOUND' || err?.status === 404) {
            console.log('⚠️ [Pre-Registration] No professional profile found - user can proceed');
            this.isCheckingStatus.set(false);
            this.showForm.set(true);
            this.loadTipologieOptions();
            this.loadBodyworkOptions();
            this.loadPersonTypeOptions();
            this.loadCompanyTypeOptions();
          } else {
            // Other error - log and show form anyway
            console.error('❌ [Pre-Registration] Error checking profile:', err);
            this.isCheckingStatus.set(false);
            this.showForm.set(true);
            this.loadTipologieOptions();
            this.loadBodyworkOptions();
            this.loadPersonTypeOptions();
            this.loadCompanyTypeOptions();
          }
        },
      });
      return;
    }
    
    // For non-professional users (AXA/backoffice): use old invitation service logic
    const userEmail = this.getCurrentUserEmail();
    
    if (!userEmail) {
      // No user logged in, show form (for demo purposes)
      this.isCheckingStatus.set(false);
      this.showForm.set(true);
      this.loadTipologieOptions();
      this.loadBodyworkOptions();
      this.loadPersonTypeOptions();
      this.loadCompanyTypeOptions();
      return;
    }

    // Check registration status via invitation service
    this.invitationService.getRegistrationStatus(userEmail).subscribe({
      next: (status) => {
        this.isCheckingStatus.set(false);
        
        if (status.hasCompletedPreRegistration) {
          // User has already completed pre-registration, redirect to next step
          this.messageService.add({
            severity: 'info',
            summary: 'Reindirizzamento',
            detail: 'Pre-registrazione già completata. Reindirizzamento agli step successivi...',
            life: 3000,
          });
          
          setTimeout(() => {
            this.router.navigate(['/axa/professionals-registration-progress']);
          }, 1500);
        } else {
          // User needs to complete pre-registration
          this.showForm.set(true);
          this.loadTipologieOptions();
          this.loadBodyworkOptions();
          this.loadPersonTypeOptions();
          this.loadCompanyTypeOptions();
        }
      },
      error: (err) => {
        console.error('Error checking registration status:', err);
        this.isCheckingStatus.set(false);
        this.showForm.set(true);
        this.loadTipologieOptions();
        this.loadBodyworkOptions();
        this.loadPersonTypeOptions();
        this.loadCompanyTypeOptions();
      },
    });
  }

  /**
   * Get current user email (mock implementation)
   * TODO: Replace with real authentication service
   */
  private getCurrentUserEmail(): string | null {
    // Try to get from CIAM mock
    const ciamEmail = sessionStorage.getItem('userEmail');
    if (ciamEmail) {
      return ciamEmail;
    }

    // Try to get from regular user session
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.email || null;
      } catch {
        return null;
      }
    }

    return null;
  }

  private loadTipologieOptions() {
    this.formDropdownService
      .getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY')
      .subscribe({
        next: (response) => {
          const BACKOFFICE_ONLY_TYPES = ['NETWORK', 'BODYSHOP'];
          const isProfessional = this.isProfessionalUser();
          this.tipologieOptions.set(
            (response.data || [])
              .filter((item) => !isProfessional || !BACKOFFICE_ONLY_TYPES.includes(item.valueKey))
              .map((item) => ({
                name: item.valueDescription,
                code: item.valueKey,
              }))
          );
        },
        error: () => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare le tipologie fiduciario',
          });
        },
      });
  }

  private loadBodyworkOptions() {
    this.formDropdownService
      .getFormDropdownValues('BODYSHOP_CATEGORY', 'BODYSHOP_CATEGORY')
      .subscribe({
        next: (response) => {
          this.bodyworkOptions.set((response.data || []).map((item) => ({
            name: item.valueDescription,
            code: item.valueKey,
          })));
        },
        error: () => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare le tipologie carrozzeria',
          });
        },
      });
  }

  private loadPersonTypeOptions() {
    this.formDropdownService
      .getFormDropdownValues('PERSON_TYPE', 'PERSON_TYPE')
      .subscribe({
        next: (response) => {
          this.personTypeOptions.set((response.data || []).map((item) => ({
            label: item.valueDescription,
            value: item.valueKey,
          })));
        },
        error: () => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare le opzioni Persona Fisica/Giuridica',
          });
        },
      });
  }

  private loadCompanyTypeOptions() {
  this.formDropdownService
    .getFormDropdownValues('LEGAL_PERSON', 'LEGAL_PERSON')
    .subscribe({
      next: (response) => {
        this.companyTypeOptions.set(
          (response.data || []).map((item) => ({
            ...item,
            label: item.valueDescription,
            value: item.valueKey,
          }))
        );
      },
      error: () => {
        this.messageService.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: 'Impossibile caricare le opzioni Tipo Società',
        });
      },
    });
}

  // AXA-specific: Create and send invitation (unified operation)
  createAndSendInvitation(emailOverride?: string, givenNameOverride?: string, familyNameOverride?: string) {
    console.log('🔵 createAndSendInvitation called');
    console.log('📝 Form values:', {
      email: this.email(),
      givenName: this.givenName(),
      familyName: this.familyName(),
      confirmEmail: this.confirmEmail()
    });
    
    const emailValue = emailOverride || this.email();
    const givenNameValue = givenNameOverride || this.givenName();
    const familyNameValue = familyNameOverride || this.familyName();
    
    // Only check confirmEmail when called from DIRECT mode (no override)
    if (!emailOverride) {
      const confirmEmailValue = this.confirmEmail();
      
      // Validate all required fields
      if (!givenNameValue || !familyNameValue) {
        console.log('❌ Validation failed: missing required fields');
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Compila tutti i campi obbligatori',
          life: 5000,
        });
        return;
      }
      
      if (!confirmEmailValue) {
        console.log('❌ Validation failed: missing confirmEmail');
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Inserisci la conferma dell\'email',
          life: 5000,
        });
        return;
      }
      
      if (emailValue !== confirmEmailValue) {
        console.log('❌ Validation failed: emails do not match');
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Le email non corrispondono',
          life: 5000,
        });
        return;
      }
    }
    
    if (!emailValue || !this.isValidEmail(emailValue)) {
      console.log('❌ Validation failed: invalid email');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Inserisci un indirizzo email valido',
        life: 5000,
      });
      return;
    }

    console.log('✅ Validation passed, calling API...');
    this.isLoading.set(true);
    
    // Single API call that creates invitation AND sends email
    this.invitationService.createAndSendInvitation(
      emailValue,
      givenNameValue,
      familyNameValue
    ).subscribe({
      next: (invitation) => {
        console.log('✅ API success:', invitation);
        this.generatedInvitation.set(invitation);
        this.isLoading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Invito Creato e Inviato',
          detail: `Email di invito inviata con successo a ${emailValue}`,
          life: 5000,
        });
      },
      error: (err) => {
        console.log('❌ API error:', err);
        this.isLoading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: err?.error?.message || 'Impossibile creare e inviare l\'invito',
          life: 5000,
        });
      },
    });
  }

  // No longer needed - email is sent automatically when invitation is created

  // AXA-specific: Copy link to clipboard
  copyToClipboard() {
    const invitation = this.generatedInvitation();
    if (!invitation) return;

    navigator.clipboard.writeText(invitation.invitationLink).then(
      () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Copiato',
          detail: 'Link copiato negli appunti',
          life: 3000,
        });
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile copiare il link',
          life: 3000,
        });
      }
    );
  }

  // Reset form and invitation
  resetFormAndInvitation() {
    this.registrationMode.set(null);
    this.email.set('');
    this.confirmEmail.set('');
    this.givenName.set('');
    this.familyName.set('');
    this.generatedInvitation.set(null);
    this.showForm.set(false);
    this.resetFormData();
  }

  private resetFormData() {
    this.preRegister.set({
      type: '',
      bodyShopType: '',
      firstName: '',
      lastName: '',
      fiscalCode: '',
      vatNumber: '',
      registrationEmail: '',
      confirmRegistrationEmail: '',
      legalOrPhysical: '',
      businessName: '',
      companyType: '',
      parentProfessionalId: '',
    });
  }

  private isValidEmail(email: string): boolean {
    const emailPattern = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  // Save pre-registration data (both AXA DELEGATED and Fiduciario)
  save() {
    // Mark all fields as touched to show validation errors
    if (this.preRegistrationForm) {
      Object.keys(this.preRegistrationForm.controls).forEach(key => {
        this.preRegistrationForm.controls[key].markAsTouched();
      });
    }

    const formData = this.preRegister();

    // Validate email match (skip for professional users who don't need to provide email)
    if (!this.isProfessionalUser()) {
      if (formData.registrationEmail !== formData.confirmRegistrationEmail) {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore di validazione',
          detail: 'Le email non corrispondono',
          life: 5000
        });
        return;
      }
    }

    // Check if form is valid
    if (this.preRegistrationForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore di validazione',
        detail: 'Compila tutti i dati obbligatori',
        life: 5000
      });
      return;
    }

    // Build API payload (exclude confirmRegistrationEmail)
    // For professional users, use their CIAM email or omit email entirely
    const payload: ProfessionalPreRegistration = {
      type: formData.type as any,
      bodyShopType: formData.bodyShopType ? (formData.bodyShopType as any) : undefined,
      legalOrPhysical: formData.legalOrPhysical ? (formData.legalOrPhysical as any) : undefined,
      companyType: formData.companyType || undefined,
      businessName: formData.businessName || undefined,
      parentProfessionalId: this.parentId() ? this.parentId() : undefined,
      firstName: formData.firstName,
      lastName: formData.lastName,
      fiscalCode: formData.fiscalCode?.toUpperCase() || '',
      vatNumber: formData.vatNumber?.toUpperCase() || '',
      // Professional users: use their CIAM email, others use form email
      registrationEmail: this.isProfessionalUser() 
        ? (this.currentUser()?.email || '') 
        : formData.registrationEmail,
    };

    this.isLoading.set(true);

    // Call API
    this.doSave(payload);
  }

  /** Esegue la chiamata di pre-registrazione; accetta il flag forceSave per bypass duplicati CF/PIVA */
  private doSave(payload: ProfessionalPreRegistration): void {
    payload.parentProfessionalId = this.parentId() || payload.parentProfessionalId || undefined;
    this.professionalsService.userPreRegistration(payload).subscribe({
      next: (response) => {
        // Extract professional ID from response.data.id
        const professionalId = response?.data?.id || 'mock-professional-id';
        
        // Save professional ID in sessionStorage for subsequent wizard steps
        sessionStorage.setItem('professional_id', professionalId);
        console.log(`✅ Professional ID saved in sessionStorage: ${professionalId}`);
        
        // Mark pre-registration as completed
        this.invitationService.markPreRegistrationCompleted(
          payload.registrationEmail, 
          professionalId
        );

        // Different behavior for AXA DELEGATED vs Fiduciario
        if (this.isAXAUser() && this.registrationMode() === 'DELEGATED') {
          // AXA DELEGATED: Generate link automatically after saving
          this.isLoading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Pre-registrazione Salvata',
            detail: 'Dati salvati con successo. Generazione link in corso...',
            life: 5000
          });
          
          // Create and send invitation automatically using data from the form
          this.createAndSendInvitation(
            payload.registrationEmail,
            payload.firstName,
            payload.lastName
          );

          // Torna alla review-professional del parent se presente
          if (this.returnToUrl()) {
            setTimeout(() => this.router.navigate([this.returnToUrl()]), 2500);
          }
        } else {
          // Fiduciario: Redirect to progress page (step management handled by BE)
          this.isLoading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Pre-registrazione completata. Reindirizzamento...',
            life: 3000
          });
          
          console.log('✅ Pre-registrazione completata, reindirizzamento a progress page');
          
          setTimeout(() => {
            this.router.navigate(['/axa/professionals-registration-progress']);
          }, 2000);
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        
        // Handle duplicate resource error specifically
        if (err?.error?.errorCode === 'DUPLICATE_RESOURCE') {
          const message = (err?.error?.message || '').toLowerCase();
          const isFiscalCodeDuplicate = message.includes('fiscal code') || message.includes('fiscalcode') || message.includes('codice fiscale');
          const isVatNumberDuplicate  = message.includes('vat number') || message.includes('vatnumber') || message.includes('partita iva');

          if (isFiscalCodeDuplicate || isVatNumberDuplicate) {
            // CF o PIVA duplicati: bypassabile con conferma
            const label = isFiscalCodeDuplicate ? 'Codice Fiscale' : 'Partita IVA';
            this.pendingForceSavePayload = { ...payload };
            this.confirmationService.confirm({
              header: 'Dato già presente',
              message: `Il ${label} inserito risulta già associato ad un altro fiduciario. L'email non risulta duplicata, quindi è possibile procedere. Vuoi continuare comunque?`,
              icon: 'pi pi-exclamation-triangle',
              acceptLabel: 'Sì, continua comunque',
              rejectLabel: 'Annulla',
              rejectButtonStyleClass: 'p-button-outlined',
              accept: () => {
                if (this.pendingForceSavePayload) {
                  this.isLoading.set(true);
                  this.doSave({ ...this.pendingForceSavePayload, forceSave: true });
                  this.pendingForceSavePayload = null;
                }
              },
              reject: () => { this.pendingForceSavePayload = null; }
            });
          } else {
            // Email duplicata (o altro non bypassabile): MAI bypassabile
            this.messageService.add({
              severity: 'error',
              summary: 'Email già registrata',
              detail: 'L\'indirizzo email inserito è già associato ad un fiduciario esistente. Non è possibile procedere con questa email: utilizzane un\'altra.',
              life: 8000,
            });
          }
        } else {
          // Generic error handling
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: err?.error?.message || 'Registrazione fallita',
            life: 5000,
          });
        }
      },
    });
  }

  resetForm() {
    // Reset form validation state
    if (this.preRegistrationForm) {
      this.preRegistrationForm.resetForm();
    }

    // Reset signal data
    this.preRegister.set({
      type: '',
      bodyShopType: '',
      firstName: '',
      lastName: '',
      fiscalCode: '',
      vatNumber: '',
      registrationEmail: '',
      confirmRegistrationEmail: '',
      legalOrPhysical: '',
      companyType: '',
      businessName: '',
      parentProfessionalId: this.parentId() ? this.parentId() : '',
    });
  }
}
